/**
 * TALKTO+ Enterprise V2 Canonical Cross-Surface Reference System
 * Pure helpers for parsing, formatting, and routing talkto:// URIs with strict security boundaries.
 */

export const SUPPORTED_CANONICAL_FAMILIES = new Set([
  'evidence',
  'decision',
  'work',
  'risk',
  'signal',
  'graph',
  'workspace',
  'agent',
]);

const SAFE_ID_REGEX = /^[a-zA-Z0-9_-]+(:[a-zA-Z0-9_-]+)*$/;

/**
 * Checks if a canonical URI string or parsed object is supported and well-formed.
 */
export function isSupportedCanonicalRef(ref) {
  if (!ref || typeof ref !== 'string') return false;
  if (!ref.startsWith('talkto://')) return false;

  const parsed = parseCanonicalRef(ref);
  return parsed !== null;
}

/**
 * Parses a canonical talkto:// URI into its constituent parts.
 * Rejects invalid schemes, traversal patterns, and malicious payloads.
 */
export function parseCanonicalRef(uri) {
  if (!uri || typeof uri !== 'string') return null;
  const trimmed = uri.trim();
  if (!trimmed.startsWith('talkto://')) return null;

  // Reject traversal, script, or external URL injection
  if (trimmed.includes('..') || trimmed.includes('\\') || trimmed.toLowerCase().includes('javascript:')) {
    return null;
  }

  const withoutScheme = trimmed.slice('talkto://'.length);
  const parts = withoutScheme.split('/').filter(Boolean);
  if (parts.length === 0) return null;

  const family = parts[0].toLowerCase();
  if (!SUPPORTED_CANONICAL_FAMILIES.has(family)) return null;

  // Handle graph/node/{id} vs standard family/{id}
  if (family === 'graph') {
    if (parts.length < 3 || parts[1].toLowerCase() !== 'node') return null;
    const id = parts.slice(2).join('/');
    if (!id || !SAFE_ID_REGEX.test(id)) return null;
    return {
      scheme: 'talkto',
      family: 'graph',
      subType: 'node',
      id,
      uri: trimmed,
    };
  }

  if (parts.length < 2) return null;
  const id = parts.slice(1).join('/');
  if (!id || !SAFE_ID_REGEX.test(id)) return null;

  return {
    scheme: 'talkto',
    family,
    id,
    uri: trimmed,
  };
}

/**
 * Formats a canonical URI from family, id, and optional subType.
 */
export function formatCanonicalRef(family, id, subType = null) {
  if (!family || !id) return null;
  const fam = String(family).toLowerCase().trim();
  if (!SUPPORTED_CANONICAL_FAMILIES.has(fam)) return null;

  const cleanId = String(id).trim();
  if (!SAFE_ID_REGEX.test(cleanId)) return null;

  if (fam === 'graph') {
    return `talkto://graph/node/${cleanId}`;
  }
  return `talkto://${fam}/${cleanId}`;
}

/**
 * Resolves a canonical reference into an internal application route.
 * Guarantees safe path navigation without dynamic code execution.
 */
export function resolveCanonicalRoute(ref) {
  const parsed = typeof ref === 'string' ? parseCanonicalRef(ref) : ref;
  if (!parsed || !parsed.family || !parsed.id) return '/';

  switch (parsed.family) {
    case 'evidence':
      return `/data?view=evidence&ref=${encodeURIComponent(parsed.uri || `talkto://evidence/${parsed.id}`)}`;
    case 'decision':
      return `/work?view=decisions&ref=${encodeURIComponent(parsed.uri || `talkto://decision/${parsed.id}`)}`;
    case 'work':
      return `/work?view=actions&ref=${encodeURIComponent(parsed.uri || `talkto://work/${parsed.id}`)}`;
    case 'risk':
      return `/intelligence?view=risks&ref=${encodeURIComponent(parsed.uri || `talkto://risk/${parsed.id}`)}`;
    case 'graph':
      return `/execution?view=map&node=${encodeURIComponent(parsed.id)}`;
    case 'workspace':
      return `/execution?view=workspace&workspace_id=${encodeURIComponent(parsed.id)}`;
    case 'agent':
      return `/enterprise?view=trust&agent=${encodeURIComponent(parsed.id)}`;
    default:
      return '/';
  }
}
